import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import styles from './VerificationFiltersScreen.module.scss';
import Button from '../../../../common/components/button/Button';
import FormItem from '../../../../common/components/formItem/FormItem';
import Label from '../../../../common/components/label/Label';
import { Col, Row } from 'react-flexbox-grid';
import { SelectValueLabel } from '../../../../common/types/SelectValueLabel';
import VehiclesService from '../../../../api/vehicles/VehiclesService';
import Select from 'common/components/select/Select';
import { Controller, useForm } from 'react-hook-form';
import UsersService from 'api/users/UsersService';
import { UsersSelectItemDto } from 'api/users/models/UserDto';
import RangeInputPicker from 'common/components/rangeInputPicker/RangeInputPicker';
import Utils from 'common/services/Utils';
import { VerificationType } from 'api/carVerification/enums/VerificationType';
import { isTripLikeVerification } from 'common/utils/VerificationsUtils';
import { AlertSubjectEnum } from 'api/alerts/enums/AlertSubjectEnum';
import { AnnotationStatusEnum } from 'api/carVerification/enums/AnnotationEnum';
import SelectController from 'common/components/select/SelectController';
import { removeAccents, LOGGER_LOG_TYPE } from 'Config';
import { useParams } from 'react-router-dom';
import LocalsService from 'api/locals/LocalsService';
import VehicleSegmentsService from 'api/vehicleSegments/VehicleSegmentsService';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { useToasts } from 'react-toast-notifications';

export interface Filters {
    vehicleId?: string;
    vehicleName?: string;
    type?: VerificationType;
    driverResponsibleId?: string;
    driverResponsibleName?: string;
    startDate?: Date;
    endDate?: Date;
    interventionType?: AlertSubjectEnum;
    annotationStatus?: AnnotationStatusEnum;
    tripStartDate?: Date;
    tripEndDate?: Date;
    localIds?: string[];
    localNames?: string[];
    localOperator?: string;
    segmentId?: string;
    segmentName?: string;
    page?: number;
    orderColumn?: string;
    orderBy?: string
}

type Props = {
    filters: Filters;
    onChange?: (filters: Filters) => void;
    onFilter: (filters: Filters) => void;
    genericFilter?: boolean;
}

const VerificationFiltersScreen: React.FC<Props> = ({ filters, genericFilter = false, onChange, onFilter }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();

    const form = useForm<Filters>({ shouldUnregister: false, shouldFocusError: true, defaultValues: filters });
    const { setValue, getValues, watch } = form;
    const [vehiclesOptions, setVehicles] = useState<SelectValueLabel[]>([]);
    const [users, setUsers] = useState<SelectValueLabel[]>([]);
    const [segmentsOptions, setSegments] = useState<SelectValueLabel[]>([]);
    const [localsOptions, setLocals] = useState<SelectValueLabel[]>([]);

    const { vehicleId } = useParams<{ vehicleId: string }>();

    const typesOptions: SelectValueLabel[] = Utils.sortAlphabetically(Object.keys(VerificationType).map(type => {
        return { value: type, label: t(`car_verification.annotation_types.${type}`.toLowerCase() as any) }
    }));

    const interventionOptions: SelectValueLabel[] = Utils.sortAlphabetically(Object.keys(AlertSubjectEnum).map(interventionType => {
        return { value: interventionType, label: t(`car_verification.intervention_type.${interventionType}`.toLowerCase() as any) }
    }));

    const annotationStatusOptions: SelectValueLabel[] = Object.keys(AnnotationStatusEnum).map(status => {
        return { value: status, label: t(`car_verification.annotation_status.${status}`.toLowerCase() as any) }
    });

    const getData = async () => {
        try {
            Loading.show();
            const [vehiclesOptionsList, usersList, segmentsOptionsDB, localsOptionsDB] = await Promise.all([
                VehiclesService.catalog(),
                UsersService.getAllForCompany(),
                VehicleSegmentsService.catalog(),
                LocalsService.catalog()
            ]);

            setVehicles(vehiclesOptionsList);
            setUsers((usersList || []).map((x: UsersSelectItemDto) => ({ value: x.id || '', label: x.realName || '' })));
            setSegments(Utils.sortAlphabetically(segmentsOptionsDB));
            setLocals([{ value: '00000000-0000-0000-0000-000000000000', label: t('maintenances.list.none') as string }, ...Utils.sortAlphabetically(localsOptionsDB)]);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.VERIFICATIONS, `Couldn't get verifications filter`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        } finally {
            Loading.hide();
        }
    }

    useEffect(() => {
        getData();
    }, []);

    const clearFilters = () => {
        form.reset({
            startDate: undefined,
            endDate: undefined,
            vehicleId: vehicleId,
            vehicleName: undefined,
            type: undefined,
            driverResponsibleId: undefined,
            driverResponsibleName: undefined,
            interventionType: undefined,
            annotationStatus: undefined,
            tripStartDate: undefined,
            tripEndDate: undefined,
            localIds: [],
            localNames: [],
            localOperator: 'OR',
            segmentId: undefined,
            segmentName: undefined,
            orderColumn: 'v.verification_date',
            orderBy: 'desc',
        });
        onInputChange();
        onSubmit();
    }

    const onSubmit = () => {
        onFilter(getValues());
    }

    const onInputChange = () => {
        if (onChange) {
            onChange(form.getValues());
        }
    }

    return (
        <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className={styles.formContent}>
                <div>
                    <FormItem>
                        <Row>
                            {genericFilter && <Col xs={12} xl={6}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('vehicle.vehicle')}</Label>
                                    <Controller
                                        render={({ value }) => {
                                            return (
                                                <Select
                                                    options={vehiclesOptions}
                                                    isClearable
                                                    placeholder={t('vehicle.vehicle')}
                                                    onChange={(data: SelectValueLabel) => {
                                                        setValue('vehicleId', data?.value ?? null);
                                                        setValue('vehicleName', data?.label ?? null);
                                                        onInputChange();
                                                    }}
                                                    value={vehiclesOptions.find(x => x.value === value) ?? null}
                                                />
                                            );
                                        }}
                                        control={form.control}
                                        name="vehicleId"
                                        defaultValue={getValues('vehicleId')} />
                                </FormItem>
                            </Col>}

                            <Col xs={12} xl={6}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('common.type')}</Label>
                                    <SelectController
                                        form={form}
                                        isClearable={true}
                                        name='type'
                                        options={typesOptions}
                                        placeholder={t('common.type')}
                                        isDisabled={false}
                                        filterOption={(candidate: any, input: any) => input ? removeAccents(candidate.label).toUpperCase().includes(removeAccents(input).toUpperCase()) : true}
                                        onChangeSelect={(data: SelectValueLabel) => {
                                            setValue('type', data?.value ?? null)
                                            if (data && data.value == VerificationType.ROUTINE) {
                                                setValue('tripStartDate', undefined);
                                                setValue('tripEndDate', undefined);
                                            } else if (data && isTripLikeVerification(data.value)) {
                                                setValue('startDate', undefined);
                                                setValue('endDate', undefined);
                                            }
                                            onInputChange();
                                        }}
                                    />
                                </FormItem>
                            </Col>

                            <Col xs={12} xl={6}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('car_verification.list.driver')}</Label>
                                    <Controller
                                        render={({ value }) => {
                                            return (
                                                <Select
                                                    options={users}
                                                    isClearable
                                                    placeholder={t('common.driver')}
                                                    onChange={(data: SelectValueLabel) => {
                                                        setValue('driverResponsibleId', data?.value ?? null);
                                                        setValue('driverResponsibleName', data?.label ?? null);
                                                        onInputChange();
                                                    }}
                                                    value={users.find(x => x.value === value) ?? null}
                                                />
                                            );
                                        }}
                                        control={form.control}
                                        name="driverResponsibleId"
                                        defaultValue={getValues('driverResponsibleId')} />
                                </FormItem>
                            </Col>

                            <Col xs={12} xl={6}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('common.intervention_type')}</Label>
                                    <SelectController
                                        form={form}
                                        isClearable={true}
                                        name='interventionType'
                                        options={interventionOptions}
                                        placeholder={t('common.intervention_type')}
                                        isDisabled={false}
                                        filterOption={(candidate: any, input: any) => input ? removeAccents(candidate.label).toUpperCase().includes(removeAccents(input).toUpperCase()) : true}
                                        onChange={() => onInputChange()}
                                    />
                                </FormItem>
                            </Col>

                            <Col xs={12} xl={6}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('common.annotation_status')}</Label>
                                    <SelectController
                                        form={form}
                                        isClearable={true}
                                        name='annotationStatus'
                                        options={annotationStatusOptions}
                                        placeholder={t('common.annotation_status')}
                                        isDisabled={false}
                                        filterOption={(candidate: any, input: any) => input ? removeAccents(candidate.label).toUpperCase().includes(removeAccents(input).toUpperCase()) : true}
                                        onChange={() => onInputChange()}
                                    />
                                </FormItem>
                            </Col>
                        </Row>
                        {genericFilter && <Row>
                            <Col xs={12} xl={6}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('vehicle.type')}</Label>
                                    <Controller
                                        render={({ onChange: onSelectChange, value }) => {
                                            return (
                                                <Select
                                                    options={segmentsOptions}
                                                    isClearable
                                                    placeholder={t('vehicle.type')}
                                                    onChange={(data: SelectValueLabel) => {
                                                        onSelectChange(data?.value ?? null);
                                                        setValue('segmentName', data?.label ?? null);
                                                        onInputChange();
                                                    }}
                                                    value={segmentsOptions.find(x => x.value === value) ? { label: segmentsOptions.find(x => x.value === value)?.label ?? '', value: value } : null}
                                                />
                                            );
                                        }}
                                        control={form.control}
                                        name="segmentId"
                                        defaultValue={getValues('segmentId')} />
                                </FormItem>
                            </Col>

                            <Col xs={12} xl={6}>
                                <FormItem>
                                    <div className={styles.labelWithOperator}>
                                        <Label className={styles.bold}>{t('vehicle.local')}</Label>
                                        <div className={styles.operatorToggle}>
                                            <span title={t('common.filter_all_tooltip' as any) as string}>
                                                <Button
                                                    text={t('common.filter_all' as any) as string}
                                                    className={styles.firstChild}
                                                    preset={watch('localOperator') === 'AND' ? 'primary' : 'secondary'}
                                                    onClick={() => { setValue('localOperator', 'AND'); onInputChange(); }}
                                                    size={'small'}
                                                />
                                            </span>
                                            <span title={t('common.filter_any_tooltip' as any) as string}>
                                                <Button
                                                    text={t('common.filter_any' as any) as string}
                                                    className={styles.lastChild}
                                                    preset={watch('localOperator') !== 'AND' ? 'primary' : 'secondary'}
                                                    onClick={() => { setValue('localOperator', 'OR'); onInputChange(); }}
                                                    size={'small'}
                                                />
                                            </span>
                                        </div>
                                    </div>
                                    <Controller
                                        render={({ onChange: onSelectChange, value }) => {
                                            const selectedValues = ((value as string[]) || []).map(v => localsOptions.find(o => o.value === v)).filter(Boolean) as SelectValueLabel[];
                                            return (
                                                <Select
                                                    options={localsOptions}
                                                    isMulti
                                                    isClearable
                                                    placeholder={t('vehicle.local')}
                                                    onChange={(data: SelectValueLabel[]) => {
                                                        const values = data ? data.map(d => d.value) : [];
                                                        const names = data ? data.map(d => d.label) : [];
                                                        onSelectChange(values.length > 0 ? values : []);
                                                        setValue('localNames', names.length > 0 ? names : []);
                                                        onInputChange();
                                                    }}
                                                    value={selectedValues}
                                                />
                                            );
                                        }}
                                        control={form.control}
                                        name="localIds"
                                        defaultValue={getValues('localIds')} />
                                </FormItem>
                            </Col>
                        </Row>}
                        <Row>
                            <Col xs={12}>
                                <RangeInputPicker
                                    label={t('car_verification.filter_date')}
                                    onChange={(startValue: any, endValue: any) => {
                                        setValue('startDate', startValue ? moment(startValue).toDate() : undefined);
                                        setValue('endDate', endValue ? moment(endValue).toDate() : undefined);
                                        if (startValue || endValue) {
                                            setValue('type', VerificationType.ROUTINE)
                                            setValue('tripStartDate', undefined)
                                            setValue('tripEndDate', undefined)
                                        }
                                        onInputChange();
                                    }}
                                    defaultStartValue={getValues('startDate')}
                                    defaultEndValue={getValues('endDate')}
                                    isDatePicker
                                    disabled={isTripLikeVerification(watch('type'))}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={12}>
                                <RangeInputPicker
                                    label={t('car_verification.trip_filter_date')}
                                    onChange={(startValue: any, endValue: any) => {
                                        setValue('tripStartDate', startValue ? moment(startValue).toDate() : undefined);
                                        setValue('tripEndDate', endValue ? moment(endValue).toDate() : undefined);
                                        if (startValue || endValue) {
                                            if (!isTripLikeVerification(getValues('type'))) {
                                                setValue('type', undefined);
                                            }
                                            setValue('startDate', undefined)
                                            setValue('endDate', undefined)
                                        }
                                        onInputChange();
                                    }}
                                    defaultStartValue={getValues('tripStartDate')}
                                    defaultEndValue={getValues('tripEndDate')}
                                    isDatePicker
                                    disabled={watch('type') == VerificationType.ROUTINE}
                                />
                            </Col>
                        </Row>


                    </FormItem>
                </div>
                <div className={styles.buttonsFooter}>
                    <FormItem>
                        <Button
                            text={t('common.clear')}
                            size={'normal'}
                            preset={'secondary'}
                            onClick={clearFilters}
                            type='reset' />
                        <Button
                            type='submit'
                            text={t('common.apply')}
                            size={'normal'}
                        />
                    </FormItem>
                </div>
            </div>
        </form>
    );
}

export default memo(VerificationFiltersScreen);